// Bridge between Clerk's React hooks and the non-React parts of the client.
//
// The API client and the WebSocket both need a fresh session token, but neither
// is a component and neither can call useAuth(). Rather than threading a token
// through every call site — where it would inevitably go stale, because Clerk
// session tokens live about a minute — they ask this module for one at the
// moment they need it, and <AuthBridge> keeps the getter pointed at Clerk.
//
// The indirection is what makes token refresh invisible: Clerk's getToken()
// returns a current token or mints one, so a request made an hour into a session
// carries a valid credential without anything here tracking expiry.

type TokenGetter = () => Promise<string | null>

let getToken: TokenGetter = async () => null

/** setTokenGetter is called once by <AuthBridge> when Clerk is ready. */
export function setTokenGetter(fn: TokenGetter) {
  getToken = fn
}

/** authToken returns a current session token, or null when signed out. */
export async function authToken(): Promise<string | null> {
  try {
    return await getToken()
  } catch {
    // A failure to mint a token is indistinguishable from being signed out as
    // far as the caller is concerned: either way the request will 401 and the
    // UI will show the sign-in screen.
    return null
  }
}

/** authHeaders returns the Authorization header, or {} when signed out. */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await authToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}
